import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Dialog, NavBar, Toast} from 'antd-mobile';
import {getNavBarContainerStyle, getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";

import PhotoViewer from '../components/PhotoViewer';
import MultiPhotoSelectorView from '../components/MultiPhotoSelectorView';
import InfoItem from '../components/InfoItem';
import {PhotoType, PositionType} from '../services/PhotoService';
import multiPhotoService from '../services/MultiPhotoService';
import {NativePermissions} from '../components/PhotoNativeBridge';
import EmptyState from "../../../components/EmptyState";
import {timestampToDate} from "../../../utils/dateTimeUtils";
import {formatLocalizedDateTime} from "../../../utils/dateFormatUtils";
import {useDeliveryMethodI18nConfig, useDeliveryTaskOrder, useDeliveryTaskOrderOperations, useFromMapMode, useTempScanPhotos} from '../store/deliveryStore';
import i18n, {t} from "../../../i18n";
import {openCamera} from "../../../utils/appRuntime";
import MultiPhotoGalleryView from '../components/MultiPhotoGalleryView';
import { formatStopNumberFromDeliveryTaskOrder } from '../../../utils/stopNumberUtils';
import geolocationService, { GeoPosition } from "../services/GeolocationService";
import OrderNoteItem from '../../../components/OrderNoteItem/OrderNoteItem';
import {maskPhoneNumber} from "../../../utils/phoneUtils";
import {FILE_UPLOAD_FAILED_KEY} from "../../../services/DeliveryDataService";
import DeliveryMethodSelector from '../components/DeliveryMethodSelector';
import {getLastDeliveryMethod, setLastDeliveryMethod} from '../utils/deliveryMethodStorage';

const kMinSelectorPhotoLength = 3
const kMaxSelectorPhotoLength = 6

interface DeliveryTemporaryProps {}

const DeliveryTemporaryPage: React.FC<DeliveryTemporaryProps> = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [photosArray, setPhotosArray] = useState<PhotoType[]>([]);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasAutoOpenedCamera, setHasAutoOpenedCamera] = useState<boolean>(false); // 标记是否已自动打开相机

    // 从 location.state 获取参数
    const scanResults = location.state?.scanResults || [];
    const deliveryTaskOrderIdList = location.state?.deliveryTaskOrderIdList || [];
    
    // 获取第一个订单的信息（单订单场景）
    const deliveryTaskOrderId = deliveryTaskOrderIdList[0];
    const trackingNumber = scanResults[0]?.trackingNumber;
    
    const {deliveryTaskOrder, loading} = useDeliveryTaskOrder(deliveryTaskOrderId);
    const {handleDelivered} = useDeliveryTaskOrderOperations();
    const { fromMapMode, setFromMapMode } = useFromMapMode();
    const { getTempScanPhotos, clearTempScanPhotos } = useTempScanPhotos();
    sessionStorage.setItem('returningFromDetail', 'true');

    // 检查是否来自地图模式（Web地图）
    const isFromMapMode = fromMapMode || location.state?.fromMapMode;
    // 获取来源页面的 activeTab（用于返回列表页时恢复状态）
    const activeTab = location.state?.activeTab;
    //妥投方式相关参数
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<string | null>(null);
    const deliveryMethodI18nConfig = useDeliveryMethodI18nConfig();

    // 图片只从 Store 获取并初始化 photosArray；退出页面或组件卸载时清空
    useEffect(() => {
        const storePhotos = getTempScanPhotos();
        const trackingNum = scanResults[0]?.trackingNumber;
        const initialPhotos: PhotoType[] = [];
        if (trackingNum && storePhotos.has(trackingNum)) {
            const base64 = storePhotos.get(trackingNum)!;
            initialPhotos.push({
                url: base64,
                objectKey: FILE_UPLOAD_FAILED_KEY,
                latitude: 0,
                longitude: 0,
                capturedAt: Date.now(),
                source: 'camera' as const
            });
        }
        if (initialPhotos.length > 0) {
            setPhotosArray(initialPhotos);
        }
        return () => {
            clearTempScanPhotos();
        };
    }, [scanResults]);

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 自动打开拍照功能（如果照片数量不足）
    useEffect(() => {
        // 只在首次进入且还未自动打开相机时执行
        if (!hasAutoOpenedCamera) {
            // 延迟执行，确保 photosArray 已经从 scanResults 加载完成
            const timer = setTimeout(() => {
                handleMultiPhotoGalleryBurstCapture();
                setHasAutoOpenedCamera(true); // 标记已打开
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [photosArray, hasAutoOpenedCamera]); // 依赖于 photosArray 的变化

    // 默认选中：上次完成妥投时的选择 > 第一种妥投方式
    useEffect(() => {
        if (deliveryMethodI18nConfig.length > 0 && selectedDeliveryMethod === null) {
            const stored = getLastDeliveryMethod();
            const code = stored && deliveryMethodI18nConfig.some(o => o.code === stored)
                ? stored
                : deliveryMethodI18nConfig[0].code;
            setSelectedDeliveryMethod(code);
        }
    }, [deliveryMethodI18nConfig, selectedDeliveryMethod]);


    // 新版删除多图画廊中的照片
    const deletePhotoArray = (index: number) => {
        Dialog.confirm({
            content: t('pages.delivery.detail.deletePhotoTitle'),
            confirmText: t('pages.delivery.detail.deletePhotoDelete'),
            cancelText: t('pages.delivery.detail.deletePhotoCancel'),
            onConfirm: () => {
                multiPhotoService.handleDeletePhotoArray(index, (updateFn) => {
                    setPhotosArray((prev) => updateFn(prev));
                });
            },
        });
    };

    // 处理多图画廊的连拍功能（新版本数组形式）
    const handleMultiPhotoGalleryBurstCapture = () => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);

        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;

        openCamera(remainingPhotoCount, minSelectionCount, t('pages.delivery.detail.requiredPhotos'), async (photos: string[]) => {
            try {
                const photosWithPosition: Array<{ photo: string; position: GeoPosition | null }> = [];

                for (const photo of photos) {
                    let currentPosition: GeoPosition | null = {
                        latitude: 0,
                        longitude: 0,
                        accuracy: 0,
                        timestamp: 0,
                    };
                    photosWithPosition.push({photo, position: currentPosition});
                }

                await multiPhotoService.handleMultiPhotoSelectionArrayWithIndividualPositionsBatched(
                    photosWithPosition,
                    (updateFn) => setPhotosArray(updateFn),
                    'camera'
                );
            } catch (error) {
                console.error('多图片处理失败:', error);
                Toast.show({
                    content: t('pages.delivery.temporary.common.multiPhotoProcessFailed'),
                    position: 'center'
                });
            }
        });
    };

    // 保存本地缓存（无网络时）
    const saveOrderToLocal = async (
        latestPhotosArray: PhotoType[],
        completionPosition: GeoPosition | null
    ) => {
        try {
            // 将照片数据转换为FailedDeliveryOrder需要的格式
            const photosForSaving = latestPhotosArray.map((photo, index) => ({
                    type: `photo_${index}` as PositionType,
                    photo: photo
                }));

            const failedOrder = {
                trackingNumber,
                updatedAt: Date.now(),
                photos: photosForSaving,
                location: completionPosition,
                stopNumber: deliveryTaskOrder?.stopNumber?.toString() || '0',
                subStopNumber: deliveryTaskOrder?.subStopNumber,
                id: deliveryTaskOrderId!,
                type: 'delivered' as const,
                completionLocation: completionPosition,
                deliveredAt: new Date().toISOString(),
                ...(selectedDeliveryMethod ? { deliveryMethod: selectedDeliveryMethod } : {})
            };

            console.log('💾 保存到本地缓存:', failedOrder);
            // 使用 DeliveryDataService 保存失败订单
            await import('../../../services/DeliveryDataService').then(module => {
                return module.default.saveDeliveryOrder(failedOrder);
            });
        } catch (error) {
            console.error('❌ 保存本地缓存失败:', error);
        }
    };

    const handleCompleteDelivery = async () => {
        setIsLoading(true);

        let completionPosition: GeoPosition | null = null;

        try {
            // 获取完成时的实时经纬度（强制获取最新位置，不使用缓存）
            try {
                completionPosition = await geolocationService.getFreshPosition();
                console.log('📍 完成派送时获取到最新位置:', completionPosition);
            } catch (error) {
                console.warn('⚠️ 获取位置失败，使用缓存位置:', error);
            }

            //仅执行乐观提交逻辑，不进行网络请求
            await handleDelivered({
                trackingNumber: trackingNumber,
                location: {
                    latitude: completionPosition?.latitude || 0.0,
                    longitude: completionPosition?.longitude || 0.0
                },
                deliveredAt: new Date().toISOString(),
                podImageS3KeysJson: [],
                id: deliveryTaskOrderId
            }, true);

            // 直接保存包裹数据到缓存
            await saveOrderToLocal(
                photosArray,
                completionPosition
            );

            clearTempScanPhotos();

            // 完成妥投时持久化本次选择的妥投方式，供下次自动勾选
            if (selectedDeliveryMethod) {
                setLastDeliveryMethod(selectedDeliveryMethod);
            }

            // 根据导航来源决定返回路径
            if (isFromMapMode) {
                setFromMapMode(false); // 清除导航来源
                // 使用兼容的导航方式
                sessionStorage.setItem('returningFromDetail', 'true');
                sessionStorage.setItem('pickUpSuccess', 'true');
                navigate("/delivery/temporary/map-mode");
            } else {
                // 返回派送列表页面，并恢复 activeTab
                navigate("/delivery/delivering/temporary", {
                    state: {
                        activeTab: activeTab
                    }
                });
            }

        } catch (error: any) {
            Toast.show({content: t('pages.delivery.detail.operationFailed'), position: 'center'});
        } finally {
            setIsLoading(false);
        }
    };

    // 处理返回按钮点击
    const handleBack = () => {
        if (isFromMapMode) {
            // 返回地图模式页面
            setFromMapMode(false);
            sessionStorage.setItem('returningFromDetail', 'true');
            navigate("/delivery/temporary/map-mode");
        } else {
            // 返回派送列表页面，并恢复 activeTab
            navigate("/delivery/delivering/temporary", {
                state: {
                    activeTab: activeTab
                }
            });
        }
    };

    if (loading || !deliveryTaskOrder) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>
                        {trackingNumber}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }

    const address = [
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.city || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.state || '',
        deliveryTaskOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
        'US'
    ].filter(Boolean).join(' ');
    const fullName = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.fullName;
    const phone = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.phoneNumber;
    const extension = deliveryTaskOrder.orderWithAddress?.deliveryAddress?.extension;
    const routeNumber = deliveryTaskOrder.routeName;
    const deliverAt = deliveryTaskOrder.orderWithAddress?.order?.deliverAt;
    const deliveryAttempts = deliveryTaskOrder.orderWithAddress?.order?.deliveryAttempts || 0;

    // 图片多选
    const multiPhotoSelectorView = (() => {
        // 计算剩余可拍照片数量
        const currentPhotoCount = photosArray.length;
        const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);

        // 动态计算最小选择数量：如果当前照片数已经超过最小要求，则最小选择为0
        const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;

        return (
            <MultiPhotoSelectorView
                minPhotos={minSelectionCount}
                maxPhotos={remainingPhotoCount}
                onPhotoSelected={async (photos: string[]) => {
                    try {
                        let currentPosition: GeoPosition | null = {
                            latitude: 0,
                            longitude: 0,
                            accuracy: 0,
                            timestamp: 0
                        };

                        const result = await multiPhotoService.handleMultiPhotoSelectionArrayWithBatched(
                            photos,
                            (updateFn) => setPhotosArray(updateFn),
                            currentPosition,
                            'gallery' // 相册选择来源：相册
                        );

                        if (!result.success) {
                            console.warn('部分图片上传失败:', result.errors);
                        }
                    } catch (error) {
                        console.error('多图片处理失败:', error);
                    }
                }}
            />
        );
    })()

    // 判断按钮是否禁用
    const buttonDisabled = (photosArray.length < kMinSelectorPhotoLength || isLoading);

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>{trackingNumber}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 订单信息 */}
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.orderInformation')}</h2>
                    <div className="mb-1">
                        <InfoItem label={`${t('pages.delivery.detail.trackingNumber')}`} value={trackingNumber}/>
                        <InfoItem label={`${t('pages.delivery.detail.stopNo')}`} value={deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0'}/>
                        <InfoItem label={`${t('pages.delivery.detail.routeNo')}`} value={routeNumber}/>
                        <InfoItem label={`${t('pages.delivery.detail.assignedTime')}`}
                                  value={deliverAt ? formatLocalizedDateTime(timestampToDate(deliverAt)) : ''}/>
                        <InfoItem label={`${t('pages.delivery.detail.deliveryAttempt')}`}
                                  value={`${deliveryAttempts} out of 3`}/>
                        <OrderNoteItem
                            orderNote={deliveryTaskOrder?.orderNote}
                            currentLanguage={i18n.language}
                        />
                    </div>
                    {/* 客户信息 */}
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.customerInformation')} </h2>
                    <div>
                        <InfoItem label={`${t('pages.delivery.detail.name')}`} value={fullName || ''} />
                        <InfoItem label={`${t('pages.delivery.detail.phoneNumber')}`} value={`${maskPhoneNumber(phone)}${extension ? `-${extension}` : ''}`} />
                        <InfoItem label={`${t('pages.delivery.detail.address')}`} value={address} />
                    </div>
                </div>

                {/* 妥投方式 */}
                {deliveryMethodI18nConfig.length > 0 && (
                    <DeliveryMethodSelector
                        options={deliveryMethodI18nConfig}
                        value={selectedDeliveryMethod}
                        onChange={setSelectedDeliveryMethod}
                    />
                )}
                {/* 照片上传区域 */}
                <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.delivery.detail.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {t('pages.delivery.detail.photoRequirement')}
                    </p>
                    {/* 多图画廊展示 >=1.2.0 */}
                    {<div className="mt-4">
                        <MultiPhotoGalleryView
                            photos={photosArray
                                .filter((photo): photo is PhotoType => photo?.url !== undefined)
                                .map(photo => photo.url)
                            }
                            onDelete={(index: number) => {
                                deletePhotoArray(index);
                            }}
                            maxDisplayCount={6}
                            onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
                        />
                    </div>}
                    {/* 相册上传 >=1.2.0 */}
                    {photosArray.length < kMaxSelectorPhotoLength && <div className="mt-4">
                        {multiPhotoSelectorView}
                    </div>}
                </div>

                {/* 查看大图 */}
                <PhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            {/* 提交按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={buttonDisabled}
                    onClick={handleCompleteDelivery}
                >
                    {t('pages.delivery.detail.complete')}
                </Button>
            </div>
        </div>
    );
};

export default DeliveryTemporaryPage;
